// Language configurations
const LANGUAGE_CONFIGS = {
    persian: {
        currency: {
            major: "تومان",
            minor: "ریال"
        },
        numbers: {
            yekan: ["", "یک", "دو", "سه", "چهار", "پنج", "شش", "هفت", "هشت", "نه"],
            yekanZero: ["صفر", "یک", "دو", "سه", "چهار", "پنج", "شش", "هفت", "هشت", "نه"],
            dahgan: ["", "", "بیست", "سی", "چهل", "پنجاه", "شصت", "هفتاد", "هشتاد", "نود"],
            dah: ["ده", "یازده", "دوازده", "سیزده", "چهارده", "پانزده", "شانزده", "هفده", "هجده", "نوزده"],
            sadgan: ["", "صد", "دویست", "سیصد", "چهارصد", "پانصد", "ششصد", "هفتصد", "هشتصد", "نهصد"]
        },
        scales: {
            million: "میلیون",
            thousand: "هزار"
        },
        connector: " و ",
        zero: "صفر"
    },
    
    english: {
        currency: {
            major: "toman",
            minor: "rial"
        },
        numbers: {
            yekan: ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"],
            yekanZero: ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"],
            dahgan: ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"],
            dah: ["ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen"],
            sadgan: ["", "one hundred", "two hundred", "three hundred", "four hundred", "five hundred", "six hundred", "seven hundred", "eight hundred", "nine hundred"]
        },
        scales: {
            million: "million",
            thousand: "thousand"
        },
        connector: " ",
        zero: "zero"
    },
    
    arabic: {
        currency: {
            major: "تومان",
            minor: "ریال"
        },
        numbers: {
            yekan: ["", "واحد", "اثنان", "ثلاثة", "أربعة", "خمسة", "ستة", "سبعة", "ثمانية", "تسعة"],
            yekanZero: ["صفر", "واحد", "اثنان", "ثلاثة", "أربعة", "خمسة", "ستة", "سبعة", "ثمانية", "تسعة"],
            dahgan: ["", "", "عشرون", "ثلاثون", "أربعون", "خمسون", "ستون", "سبعون", "ثمانون", "تسعون"],
            dah: ["عشرة", "أحد عشر", "اثنا عشر", "ثلاثة عشر", "أربعة عشر", "خمسة عشر", "ستة عشر", "سبعة عشر", "ثمانية عشر", "تسعة عشر"],
            sadgan: ["", "مائة", "مائتان", "ثلاثمائة", "أربعمائة", "خمسمائة", "ستمائة", "سبعمائة", "ثمانمائة", "تسعمائة"]
        },
        scales: {
            million: "مليون",
            thousand: "ألف"
        },
        connector: " و ",
        zero: "صفر"
    }
};

export const numberToText = (rials, language = 'persian') => {
    const config = LANGUAGE_CONFIGS[language];
    
    if (!config) {
        throw new Error(`Language '${language}' is not supported. Available languages: ${Object.keys(LANGUAGE_CONFIGS).join(', ')}`);
    }

    // Convert rials to tomans (divide by 10) - same logic for all languages
    const tomans = Math.floor(rials / 10);
    const remainingRials = rials % 10;

    // If the amount is less than 10 rials, show in rials for all languages
    if (rials < 10) {
        return `${config.numbers.yekanZero[rials]} ${config.currency.minor}`;
    }

    // For amounts 10 rials and above, convert to tomans for all languages
    if (tomans === 0) return `${config.zero} ${config.currency.major}`;

    const convertNumber = (num, lang) => {
        const langConfig = LANGUAGE_CONFIGS[lang];
        
        if (num === 0) return "";
        if (num < 10) return langConfig.numbers.yekan[num];
        if (num < 20) return langConfig.numbers.dah[num - 10];
        if (num < 100) {
            const ones = num % 10;
            const tens = Math.floor(num / 10);
            return ones > 0 ? 
                `${langConfig.numbers.dahgan[tens]}${langConfig.connector}${langConfig.numbers.yekan[ones]}` : 
                langConfig.numbers.dahgan[tens];
        }
        if (num < 1000) {
            const hundreds = Math.floor(num / 100);
            const remainder = num % 100;
            if (remainder === 0) return langConfig.numbers.sadgan[hundreds];
            return `${langConfig.numbers.sadgan[hundreds]}${langConfig.connector}${convertNumber(remainder, lang)}`;
        }
        return "";
    };

    let result = "";

    // Handle millions
    const millions = Math.floor(tomans / 1000000);
    if (millions > 0) {
        const millionText = millions === 1 ? config.numbers.yekan[1] : convertNumber(millions, language);
        result += `${millionText} ${config.scales.million}`;
        if (tomans % 1000000 !== 0) result += config.connector;
    }

    // Handle thousands
    const thousands = Math.floor((tomans % 1000000) / 1000);
    if (thousands > 0) {
        const thousandText = thousands === 1 ? config.numbers.yekan[1] : convertNumber(thousands, language);
        result += `${thousandText} ${config.scales.thousand}`;
        if (tomans % 1000 !== 0) result += config.connector;
    }

    // Handle hundreds and smaller numbers
    const remainder = tomans % 1000;
    if (remainder > 0 || result === "") {
        result += convertNumber(remainder, language);
    }

    return `${result} ${config.currency.major}`;
};

// Convenience functions for each language
export const numberToPersianText = (rials) => numberToText(rials, 'persian');
export const numberToEnglishText = (rials) => numberToText(rials, 'english');
export const numberToArabicText = (rials) => numberToText(rials, 'arabic');

// Example usage:
/*
console.log(numberToPersianText(12345)); // دوازده هزار و سیصد و چهل و پنج تومان
console.log(numberToEnglishText(12345)); // one thousand two hundred thirty four toman
console.log(numberToArabicText(12345)); // ألف و مائتان و أربعة و ثلاثون تومان

// Or use the main function with language parameter
console.log(numberToText(12345, 'persian'));
console.log(numberToText(12345, 'english'));
console.log(numberToText(12345, 'arabic'));

// For amounts less than 10 rials:
console.log(numberToText(5, 'persian')); // پنج ریال
console.log(numberToText(5, 'english')); // five rial
console.log(numberToText(5, 'arabic')); // خمسة ریال
*/













// // Language configurations
// const LANGUAGE_CONFIGS = {
//     persian: {
//         currency: {
//             major: "تومان",
//             minor: "ریال"
//         },
//         numbers: {
//             yekan: ["", "یک", "دو", "سه", "چهار", "پنج", "شش", "هفت", "هشت", "نه"],
//             yekanZero: ["صفر", "یک", "دو", "سه", "چهار", "پنج", "شش", "هفت", "هشت", "نه"],
//             dahgan: ["", "", "بیست", "سی", "چهل", "پنجاه", "شصت", "هفتاد", "هشتاد", "نود"],
//             dah: ["ده", "یازده", "دوازده", "سیزده", "چهارده", "پانزده", "شانزده", "هفده", "هجده", "نوزده"],
//             sadgan: ["", "صد", "دویست", "سیصد", "چهارصد", "پانصد", "ششصد", "هفتصد", "هشتصد", "نهصد"]
//         },
//         scales: {
//             million: "میلیون",
//             thousand: "هزار"
//         },
//         connector: " و ",
//         zero: "صفر"
//     },
    
//     english: {
//         currency: {
//             major: "toman",
//             minor: "rial"
//         },
//         numbers: {
//             yekan: ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"],
//             yekanZero: ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"],
//             dahgan: ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"],
//             dah: ["ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen"],
//             sadgan: ["", "one hundred", "two hundred", "three hundred", "four hundred", "five hundred", "six hundred", "seven hundred", "eight hundred", "nine hundred"]
//         },
//         scales: {
//             million: "million",
//             thousand: "thousand"
//         },
//         connector: " ",
//         zero: "zero"
//     },
    
//     arabic: {
//         currency: {
//             major: "تومان",
//             minor: "ریال"
//         },
//         numbers: {
//             yekan: ["", "واحد", "اثنان", "ثلاثة", "أربعة", "خمسة", "ستة", "سبعة", "ثمانية", "تسعة"],
//             yekanZero: ["صفر", "واحد", "اثنان", "ثلاثة", "أربعة", "خمسة", "ستة", "سبعة", "ثمانية", "تسعة"],
//             dahgan: ["", "", "عشرون", "ثلاثون", "أربعون", "خمسون", "ستون", "سبعون", "ثمانون", "تسعون"],
//             dah: ["عشرة", "أحد عشر", "اثنا عشر", "ثلاثة عشر", "أربعة عشر", "خمسة عشر", "ستة عشر", "سبعة عشر", "ثمانية عشر", "تسعة عشر"],
//             sadgan: ["", "مائة", "مائتان", "ثلاثمائة", "أربعمائة", "خمسمائة", "ستمائة", "سبعمائة", "ثمانمائة", "تسعمائة"]
//         },
//         scales: {
//             million: "مليون",
//             thousand: "ألف"
//         },
//         connector: " و ",
//         zero: "صفر"
//     }
// };

// export const numberToText = (rials, language = 'persian') => {
//     const config = LANGUAGE_CONFIGS[language];
    
//     if (!config) {
//         throw new Error(`Language '${language}' is not supported. Available languages: ${Object.keys(LANGUAGE_CONFIGS).join(', ')}`);
//     }

//     // Convert rials to major currency unit (divide by 10 for Persian/tomans)
//     let majorUnit, remainingMinor;
    
//     if (language === 'persian') {
//         // Persian uses tomans (rials / 10)
//         majorUnit = Math.floor(rials / 10);
//         remainingMinor = rials % 10;
//     } else {
//         // For other currencies, assume direct conversion
//         majorUnit = Math.floor(rials / 100); // assuming cents/minor units
//         remainingMinor = rials % 100;
//     }

//     // If the amount is less than the major unit threshold
//     if ((language === 'persian' && rials < 10) || (language !== 'persian' && rials < 100)) {
//         return `${config.numbers.yekanZero[rials]} ${language === 'persian' ? config.currency.minor : config.currency.minor}`;
//     }

//     if (majorUnit === 0) return `${config.zero} ${config.currency.major}`;

//     const convertNumber = (num, lang) => {
//         const langConfig = LANGUAGE_CONFIGS[lang];
        
//         if (num === 0) return "";
//         if (num < 10) return langConfig.numbers.yekan[num];
//         if (num < 20) return langConfig.numbers.dah[num - 10];
//         if (num < 100) {
//             const ones = num % 10;
//             const tens = Math.floor(num / 10);
//             return ones > 0 ? 
//                 `${langConfig.numbers.dahgan[tens]}${langConfig.connector}${langConfig.numbers.yekan[ones]}` : 
//                 langConfig.numbers.dahgan[tens];
//         }
//         if (num < 1000) {
//             const hundreds = Math.floor(num / 100);
//             const remainder = num % 100;
//             if (remainder === 0) return langConfig.numbers.sadgan[hundreds];
//             return `${langConfig.numbers.sadgan[hundreds]}${langConfig.connector}${convertNumber(remainder, lang)}`;
//         }
//         return "";
//     };

//     let result = "";

//     // Handle millions
//     const millions = Math.floor(majorUnit / 1000000);
//     if (millions > 0) {
//         const millionText = millions === 1 ? config.numbers.yekan[1] : convertNumber(millions, language);
//         result += `${millionText} ${config.scales.million}`;
//         if (majorUnit % 1000000 !== 0) result += config.connector;
//     }

//     // Handle thousands
//     const thousands = Math.floor((majorUnit % 1000000) / 1000);
//     if (thousands > 0) {
//         const thousandText = thousands === 1 ? config.numbers.yekan[1] : convertNumber(thousands, language);
//         result += `${thousandText} ${config.scales.thousand}`;
//         if (majorUnit % 1000 !== 0) result += config.connector;
//     }

//     // Handle hundreds and smaller numbers
//     const remainder = majorUnit % 1000;
//     if (remainder > 0 || result === "") {
//         result += convertNumber(remainder, language);
//     }

//     return `${result} ${config.currency.major}`;
// };

// // Convenience functions for each language
// export const numberToPersianText = (rials) => numberToText(rials, 'persian');
// export const numberToEnglishText = (rials) => numberToText(rials, 'english');
// export const numberToArabicText = (rials) => numberToText(rials, 'arabic');

// // Example usage:
// /*
// console.log(numberToPersianText(12345)); // دوازده هزار و سیصد و چهل و پنج تومان
// console.log(numberToEnglishText(12345)); // one hundred twenty three dollars
// console.log(numberToArabicText(12345)); // مائة و ثلاثة و عشرون ريال

// // Or use the main function with language parameter
// console.log(numberToText(12345, 'persian'));
// console.log(numberToText(12345, 'english'));
// console.log(numberToText(12345, 'arabic'));
// */